import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import {
  getMyAvailableTemplates,
  generateMyDocument,
  MyDocumentTemplateView,
  DocumentTemplateCategory,
} from '../../../../api/General/documentTemplates';
import { downloadHtmlAsPdf } from '../../../../utils/htmlToPdf';

// Each category gets a recognizable leading icon so the shelf reads at a glance.
const CATEGORY_META: Record<string, { icon: IconName; label: string }> = {
  [DocumentTemplateCategory.Contract]: { icon: 'FileSignature', label: 'Contract' },
  [DocumentTemplateCategory.Certificate]: { icon: 'Award', label: 'Certificate' },
  [DocumentTemplateCategory.Letter]: { icon: 'Mail', label: 'Letter' },
  [DocumentTemplateCategory.Settlement]: { icon: 'Scale', label: 'Settlement' },
  [DocumentTemplateCategory.Other]: { icon: 'FileText', label: 'Other' },
};

// Self-service section embedded in the profile Documents page (own profile only):
// lists HR-published templates and downloads a PDF filled with the caller's own data.
const OnDemandDocuments: React.FC = () => {
  const { t } = useTranslation();

  const [templates, setTemplates] = useState<MyDocumentTemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<Record<number, string | null>>({});

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getMyAvailableTemplates();
      if (res.errors.length === 0) {
        setTemplates(res.payload || []);
      } else {
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDownload = async (tpl: MyDocumentTemplateView) => {
    setDownloadingId(tpl.id);
    setRowError((prev) => ({ ...prev, [tpl.id]: null }));
    try {
      // The server fills the template with the caller's own data and returns HTML; the browser
      // renders it to a PDF (correct Arabic shaping/bidi/wrapping).
      const filledHtml = await generateMyDocument(tpl.id);
      await downloadHtmlAsPdf(filledHtml, `${tpl.name}.pdf`);
    } catch (err) {
      // The interceptor parses a JSON error body, so surface the real backend message when
      // present (falls back to a friendly default otherwise).
      const message =
        (err as { errors?: { description?: string }[] })?.errors?.[0]?.description ||
        t('Could not prepare this document. Please try again.');
      setRowError((prev) => ({ ...prev, [tpl.id]: message }));
    } finally {
      setDownloadingId(null);
    }
  };

  const meta = (category: string) => CATEGORY_META[category] || CATEGORY_META[DocumentTemplateCategory.Other];

  return (
    <section className="mt-8 border-t border-border pt-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-foreground">{t('On Demand Documents')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('Download documents prepared by HR, filled in with your own information, as PDF.')}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <Icon name="AlertCircle" size={24} className="text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground">{t("We couldn't load your documents.")}</p>
          <Button variant="outline" className="mt-4" onClick={load} iconName="RefreshCw" iconPosition="left">
            {t('Try again')}
          </Button>
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
            <Icon name="FolderOpen" size={28} className="text-muted-foreground" />
          </div>
          <p className="text-base font-medium text-foreground">{t('No documents available yet')}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {t('When HR publishes a document template, it will appear here for you to download.')}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {templates.map((tpl) => {
            const m = meta(tpl.category);
            const isDownloading = downloadingId === tpl.id;
            const err = rowError[tpl.id];
            return (
              <li key={tpl.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon name={m.icon} size={20} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">{tpl.name}</p>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {t(m.label)}
                      </span>
                    </div>
                    {tpl.description && (
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{tpl.description}</p>
                    )}
                    {err && <p className="mt-1 text-xs text-destructive">{err}</p>}
                  </div>
                </div>
                <div className="flex-shrink-0 ps-[3.25rem] sm:ps-0">
                  <Button
                    onClick={() => handleDownload(tpl)}
                    loading={isDownloading}
                    disabled={isDownloading}
                    iconName="Download"
                    iconPosition="left"
                  >
                    {isDownloading ? t('Preparing...') : t('Download PDF')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default OnDemandDocuments;
